import { LockIcon } from "@/components/icons/Icon";
import { Box, Group } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BiEnvelope, BiUser } from "react-icons/bi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Select as ReactSelect } from "chakra-react-select";
import CancelButton from "src/components/buttons/CancelButton";
import ErrorMessage from "src/components/ErrorMessage";
import FormPage from "@/components/FormPage";
import SectionPanel from "@/components/SectionPanel";
import SubmitButton from "@/components/buttons/SubmitButton";
import PasswordStrengthIndicator from "@/components/PasswordStrengthIndicator";
import { FormInput } from "@/components/fields/FormInput";
import { userService } from "src/service/UserService";
import { partnerService } from "@/service/PartnerService";
import { InputGroup } from "@/components/ui/input-group"
import { Field } from "@/components/ui/field";
import { PasswordInput } from "@/components/ui/password-input";
import { Radio, RadioGroup } from "@/components/ui/radio";
import { LabelAndValueOption } from "@/data/LabelAndValueOption";
import { toaster } from "@/components/ui/toaster";

type UserAccountType = 'member' | 'extern';

interface NewUser {
    email: string;
    password: string;
    password2: string,
    firstName: string;
    lastName: string;
    accountType: UserAccountType;
    partnerId: number | null;
}

const UsersAdd = () => {
    const [searchParams] = useSearchParams();
    const preselectedPartnerId = useMemo(() => {
        const rawPartnerId = searchParams.get('partnerId');
        const partnerId = rawPartnerId ? Number(rawPartnerId) : null;
        return partnerId && Number.isInteger(partnerId) && partnerId > 0 ? partnerId : null;
    }, [searchParams]);
    const isPartnerExternUsersPanelFlow = preselectedPartnerId !== null && searchParams.get('source') === 'partnerExternUsersPanel';
    const closePath = useMemo(
        () => isPartnerExternUsersPanelFlow ? `/partners/view/${preselectedPartnerId}?tab=users` : '/organization',
        [isPartnerExternUsersPanelFlow, preselectedPartnerId]
    );

    const { control, register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm<NewUser>({
        defaultValues: {
            email: '',
            password: '',
            password2: '',
            firstName: '',
            lastName: '',
            accountType: preselectedPartnerId ? 'extern' : 'member',
            partnerId: preselectedPartnerId,
        }
    });
    const newPassword = watch('password', '');
    const newPassword2 = watch('password2', '');
    const accountType = watch('accountType', preselectedPartnerId ? 'extern' : 'member');
    const header = accountType === 'extern' ? 'Neue Partneranmeldung' : 'Neues Mitglied';
    const [serverError, setServerError] = useState('');
    const [partnerOptions, setPartnerOptions] = useState<LabelAndValueOption[]>([]);
    const [partnersLoading, setPartnersLoading] = useState(false);

    const readOnly = false;
    const navigate = useNavigate();

    useEffect(() => {
        setPartnersLoading(true);
        partnerService.listFiltered(false)
            .then(partners => setPartnerOptions(
                partners
                    .map(partner => ({ label: partner.name, value: partner.id }))
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
            ))
            .catch(() => setServerError('Partner konnten nicht geladen werden'))
            .finally(() => setPartnersLoading(false));
    }, []);

    useEffect(() => {
        if (preselectedPartnerId) {
            setValue('accountType', 'extern');
            setValue('partnerId', preselectedPartnerId);
        }
    }, [preselectedPartnerId, setValue]);

    const onSubmitInternal = useCallback(
        (p: NewUser): Promise<void> => {
            const partnerId = p.accountType === 'extern' ? p.partnerId : null;
            return userService.add(p.email, p.password, p.firstName, p.lastName, partnerId).then(() => {
                toaster.create({
                    title: 'Benutzer hinzugefügt',
                    type: 'success'
                });
                navigate(closePath);
            }).catch(err => setServerError('Fehler'));
        }, [closePath, navigate]
    );

    return (
        <FormPage title={header} description=''
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate(closePath)} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            <SectionPanel>
                {serverError && <ErrorMessage message={serverError} />}

                <FormInput
                    name="email"
                    label="E-Mail"
                    register={register}
                    error={errors.email}
                    required
                    readOnly={readOnly}
                    icon={BiEnvelope}
                    autoFocus={true}
                    rules={{
                        required: 'E-Mail muss angegeben werden',
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Ungültige E-Mail-Adresse',
                        },
                    }}
                />

                <Box>
                    <Field id="password" label='Passwort' required invalid={!!errors.password} errorText={errors.email?.message}>
                        <InputGroup w='full' startElement={<LockIcon color='orange' />}>
                            <PasswordInput {...register('password', { required: 'Passwort ist erforderlich' })} />
                        </InputGroup>
                    </Field>
                    <PasswordStrengthIndicator password={newPassword} shortScoreWord='zu kurz' scoreWords={['schwach', 'schwach', 'okay', 'gut', 'stark']} />

                    <Field id="confirmPassword" label='Passwort wiederholen' required invalid={newPassword !== newPassword2} errorText={errors.password2?.message}>
                        <InputGroup w='full' startElement={<LockIcon color='orange' />}>
                            <PasswordInput {...register('password2', {
                                validate: (value) => value === newPassword || 'Passwörter stimmen nicht überein'
                            })} />
                        </InputGroup>
                    </Field>
                </Box>

                <Box>
                    <FormInput name="firstName" label='Vorname' register={register} error={errors.firstName} required readOnly={readOnly} icon={BiUser} />
                </Box>

                <Box>
                    <FormInput name="lastName" label='Nachname' register={register} error={errors.lastName} required readOnly={readOnly} icon={BiUser} />
                </Box>

                {!isPartnerExternUsersPanelFlow && (
                    <Controller control={control} name='accountType'
                        render={({ field }) => (
                            <Field id="accountType" label='Benutzertyp' required>
                                <RadioGroup value={field.value} onValueChange={event => {
                                    const next = event.value as UserAccountType;
                                    field.onChange(next);
                                    if (next === 'member') {
                                        setValue('partnerId', null, { shouldValidate: true });
                                    }
                                }}>
                                    <Group gap={4}>
                                        <Radio value='member'>Mitglied</Radio>
                                        <Radio value='extern'>Partner</Radio>
                                    </Group>
                                </RadioGroup>
                            </Field>
                        )} />
                )}

                {accountType === 'extern' && (
                    <Controller control={control} name='partnerId'
                        rules={{ validate: (value) => value ? true : 'Partner muss ausgewählt werden' }}
                        render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
                            <Field id="partnerId" label='Partner' required disabled={readOnly} invalid={invalid} errorText={error?.message}>
                                <ReactSelect<LabelAndValueOption, false>
                                    isSearchable
                                    required
                                    isDisabled={readOnly || partnersLoading}
                                    ref={ref}
                                    name={name}
                                    onBlur={onBlur}
                                    placeholder='Partner auswählen'
                                    options={partnerOptions}
                                    onChange={val => onChange(val?.value ?? null)}
                                    value={partnerOptions.find(c => c.value === value) ?? null}
                                />
                            </Field>
                        )} />
                )}
            </SectionPanel>
        </FormPage>
    )
}

export default UsersAdd
